import { useState } from 'react';

import { ArrowRightOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip } from 'antd';

import {
  EAdmissionSurveyStatus,
  type IAdmissionApplicationsStats,
  type IAdmissionSurvey,
  useMoveToNextStageMutation,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { ConfirmModal } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { SendNotificationsModal } from '../SendNotificationsModal/SendNotificationsModal';

import styles from './ActionButtonsPanel.module.scss';

interface IProp {
  schoolId: number;
  applicationsStats: IAdmissionApplicationsStats;
  surveyData: IAdmissionSurvey;
}

export const ActionButtonsPanel = ({
  schoolId,
  surveyData,
  applicationsStats: { approved: approvedCount, rejected: rejectedCount },
}: IProp) => {
  const [isMoveStageModalOpen, setIsMoveStageMoveModalOpen] = useState(false);
  const [isSendNotificationsModalOpen, setIsSendNotificationsModalOpen] = useState(false);

  const [moveApplicationsToNextStage, { isLoading: isApplicationsMovingToNextStage }] = useMoveToNextStageMutation();

  const {
    transfer_report: { failed_count: failedTransferCount },
    status,
  } = surveyData;

  const sendApiRequest = useApiRequest();

  const moveApplicationToNextStageHandle = () =>
    sendApiRequest({
      successMessage: 'Кандидаты успешно переведены',
      apiRequestCallback: () => moveApplicationsToNextStage({ schoolId }),
      successCallback: () => setIsMoveStageMoveModalOpen(false),
    });

  return (
    <div>
      <SendNotificationsModal
        schoolId={schoolId}
        rejectedCount={rejectedCount}
        approvedCount={approvedCount}
        isModalOpen={isSendNotificationsModalOpen}
        handleCancel={() => setIsSendNotificationsModalOpen(false)}
      />
      <Spin spinning={isApplicationsMovingToNextStage}>
        <ConfirmModal
          isModalOpen={isMoveStageModalOpen}
          handleCancel={() => setIsMoveStageMoveModalOpen(false)}
          handleConfirm={moveApplicationToNextStageHandle}
          title="Перевод на следующий этап"
          description="Вы уверены, что хотите перевести участников на следующий этап?"
          shouldDisableElement={isApplicationsMovingToNextStage}
        />
      </Spin>
      <div className={styles.wrapper}>
        <Tooltip
          title={
            status === EAdmissionSurveyStatus.Draft || status === EAdmissionSurveyStatus.ReadyForNotifications
              ? 'Перевод на следующий этап станет доступен после отправки уведомлений всем участникам'
              : null
          }
        >
          <Button
            icon={<ArrowRightOutlined />}
            color="blue"
            variant="outlined"
            onClick={() => setIsMoveStageMoveModalOpen(true)}
            disabled={
              status !== EAdmissionSurveyStatus.ReadyForTransfer && status !== EAdmissionSurveyStatus.TransferInProgress
            }
          >
            Перевести одобренных на этап 2{' '}
            {status === EAdmissionSurveyStatus.TransferInProgress && `(${failedTransferCount})`}
          </Button>
        </Tooltip>
        <Tooltip
          title={
            status === EAdmissionSurveyStatus.Draft
              ? 'Отправка уведомлений станет доступна после завершения опроса и проверки всех заявок'
              : null
          }
        >
          <Button
            icon={
              <SendOutlined
                rotate={-45}
                className={styles.icon}
              />
            }
            color="blue"
            variant="solid"
            disabled={status !== EAdmissionSurveyStatus.ReadyForNotifications}
            onClick={() => setIsSendNotificationsModalOpen(true)}
          >
            Отправить уведомления
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
